/**
 * Application shell: tenant scope, navigation, and data loading.
 *
 * State is deliberately flat. The demo is a linear walk from portfolio to vendor to
 * service level, and a router would add indirection without adding capability.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchPortfolio, fetchSession, fetchSources, loginUrl, logout } from "./lib/api";
import type { Portfolio, Session, SourcesResponse } from "./lib/types";
import { SignInView } from "./views/SignInView";
import { Notice } from "./components/Chrome";
import { PortfolioView } from "./views/PortfolioView";
import { VendorView } from "./views/VendorView";
import { ServiceLevelView } from "./views/ServiceLevelView";
import { SourcesView } from "./views/SourcesView";
import { AnalystView } from "./views/AnalystView";
import { ConsoleView } from "./views/ConsoleView";
import { OnboardingView } from "./views/OnboardingView";
import { periodLabel } from "./lib/format";

type Tab = "portfolio" | "sources" | "console" | "onboarding" | "analyst";

const TABS: { key: Tab; label: string; requires?: keyof Session["capabilities"] }[] = [
  { key: "portfolio", label: "Portfolio" },
  { key: "sources", label: "Data sources" },
  { key: "console", label: "Network", requires: "view_network" },
  { key: "onboarding", label: "Add vendor", requires: "onboard_vendors" },
  { key: "analyst", label: "Analyst" },
];

export function App() {
  // Nobody is signed in until the server says so. Defaulting to a tenant would render a
  // client's name before anyone had been identified.
  const [session, setSession] = useState<Session | null>(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [tenant, setTenant] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("portfolio");
  const [vendorId, setVendorId] = useState<string | null>(null);
  const [serviceLevelId, setServiceLevelId] = useState<string | null>(null);

  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [sources, setSources] = useState<SourcesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchSession()
      .then((next) => {
        if (cancelled) return;
        setSession(next);
        // A single-client account is pinned server-side anyway; following its own scope
        // here keeps the switcher from offering a client the API would refuse.
        setTenant(next?.scope.tenant_ids?.[0] ?? null);
      })
      .catch(() => {
        if (!cancelled) setSession(null);
      })
      .finally(() => {
        if (!cancelled) setCheckingSession(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!session) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([fetchPortfolio(tenant), fetchSources()])
      .then(([nextPortfolio, nextSources]) => {
        if (cancelled) return;
        setPortfolio(nextPortfolio);
        setSources(nextSources);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setError(caught instanceof Error ? caught.message : "Could not load the portfolio.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tenant, session]);

  const vendor = useMemo(
    () => portfolio?.vendors.find((candidate) => candidate.id === vendorId) ?? null,
    [portfolio, vendorId],
  );
  const serviceLevel = useMemo(
    () => vendor?.service_levels.find((level) => level.id === serviceLevelId) ?? null,
    [vendor, serviceLevelId],
  );

  const backToPortfolio = useCallback(() => {
    setVendorId(null);
    setServiceLevelId(null);
  }, []);

  const switchTenant = useCallback((next: string | null) => {
    setTenant(next);
    setVendorId(null);
    setServiceLevelId(null);
    setTab("portfolio");
  }, []);

  if (checkingSession) {
    return <div style={{ padding: 28, color: "var(--slate)", fontSize: 13 }}>Checking session…</div>;
  }

  // No chrome before there is an identity: the header carries a client name, and naming
  // one to an unauthenticated visitor is the disclosure the scope model exists to stop.
  if (!session) {
    return <SignInView signInHref={loginUrl(window.location.pathname)} />;
  }

  return (
    <div style={{ minHeight: "100%", display: "flex", flexDirection: "column" }}>
      <header
        style={{
          borderBottom: "1px solid var(--line)",
          padding: "16px 28px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
          <span className="serif" style={{ fontSize: 20, letterSpacing: "0.04em" }}>
            ACTUOVIOUS
          </span>
          {portfolio && (
            <span style={{ fontSize: 12, color: "var(--slate)" }}>
              {periodLabel(portfolio.as_of)}
            </span>
          )}
          {session && (
            <span style={{ fontSize: 12, color: "var(--slate)" }}>
              {session.display_name} · {session.role_label}
              {session.organisation ? ` · ${session.organisation}` : ""}
            </span>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {session?.capabilities.switch_client && portfolio && portfolio.tenants.length > 1 && (
            <select
              value={tenant ?? ""}
              onChange={(event) => switchTenant(event.target.value || null)}
              aria-label="Client"
              style={{
                background: "transparent",
                border: "1px solid var(--line-strong)",
                color: "var(--parchment)",
                borderRadius: 6,
                padding: "6px 10px",
                fontSize: 12.5,
                fontFamily: "var(--sans)",
              }}
            >
              {/* The partner is attached to no single client, so an unscoped view is a
                  real state and needs its own option. Without one the control shows the
                  first client while the portfolio below spans all of them. */}
              <option value="" style={{ background: "var(--ink)" }}>
                All clients
              </option>
              {portfolio.tenants.map((option) => (
                <option key={option.id} value={option.id} style={{ background: "var(--ink)" }}>
                  {option.name}
                </option>
              ))}
            </select>
          )}

          <nav aria-label="Primary" style={{ display: "flex", gap: 6 }}>
            {/* A tab the role cannot use is absent, not disabled. The server refuses the
                request either way; showing a control that only ever fails is noise. */}
            {TABS.filter(
              (option) => !option.requires || session?.capabilities[option.requires],
            ).map((option) => (
              <button
                key={option.key}
                onClick={() => {
                  // Always unwind the drill-down. Clicking Portfolio while three levels
                  // deep has to return to the portfolio, not sit on the current screen.
                  setTab(option.key);
                  backToPortfolio();
                }}
                aria-current={tab === option.key ? "page" : undefined}
                style={{
                  background: "transparent",
                  border: `1px solid ${tab === option.key ? "var(--gold)" : "var(--line-strong)"}`,
                  color: tab === option.key ? "var(--gold)" : "var(--slate)",
                  borderRadius: 6,
                  padding: "6px 12px",
                  fontSize: 12.5,
                }}
              >
                {option.label}
              </button>
            ))}
          </nav>

          <button
            onClick={() => {
              void logout().then((url) => {
                window.location.href = url;
              });
            }}
            style={{
              background: "transparent",
              border: "1px solid var(--line-strong)",
              color: "var(--slate)",
              borderRadius: 6,
              padding: "6px 12px",
              fontSize: 12.5,
            }}
          >
            Sign out
          </button>
        </div>
      </header>

      <main style={{ flex: 1, padding: "28px", maxWidth: 1280, width: "100%", margin: "0 auto" }}>
        {loading && session && (
          <div style={{ color: "var(--slate)", fontSize: 13 }}>Running the pipeline…</div>
        )}

        {error && !loading && (
          <Notice tone="warn">
            {error} The API may not be running. Start it with{" "}
            <code>uvicorn app.main:app</code> from the backend directory.
          </Notice>
        )}

        {!loading && !error && portfolio && (
          <>
            {tab === "sources" && sources && <SourcesView sources={sources} />}
            {tab === "console" && <ConsoleView tenant={tenant} />}
            {tab === "onboarding" && (
              <OnboardingView
                tenant={tenant}
                onAdded={(vendorId) => {
                  // The portfolio is stale the moment a vendor is added, so reload it and
                  // open what was just created rather than leaving the user to find it.
                  void fetchPortfolio(tenant).then((next) => {
                    setPortfolio(next);
                    setTab("portfolio");
                    setVendorId(vendorId);
                  });
                }}
              />
            )}
            {tab === "analyst" && <AnalystView tenant={tenant} />}
            {tab === "portfolio" && (
              <>
                {vendor && serviceLevel ? (
                  <ServiceLevelView
                    vendor={vendor}
                    level={serviceLevel}
                    finding={vendor.findings.find(
                      (finding) => finding.service_level_id === serviceLevel.id,
                    )}
                    onBackToPortfolio={backToPortfolio}
                    onBackToVendor={() => setServiceLevelId(null)}
                  />
                ) : vendor ? (
                  <VendorView
                    vendor={vendor}
                    onBack={backToPortfolio}
                    onOpenServiceLevel={setServiceLevelId}
                  />
                ) : (
                  <PortfolioView portfolio={portfolio} onOpenVendor={setVendorId} />
                )}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
